import React, { ChangeEventHandler, useCallback, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { useParams } from "react-router";
import classNames from "classnames";

import {
	BrandliveEvent,
	ModuleGroups,
	PageModuleGroupModules,
	PageModuleType,
	Session,
	TranslateString,
	FeatureFlagsEnum,
} from '../../../types/working-model';
import Icon from "../../general-ui/icon";
import { ParamsProps } from '../live-event';
import {
	clearErrorBoundaryComponents} from "../../../store/actions/event/event-actions";
import { useTranslate } from "../../../i18n/useTranslationModules";
import { useAppDispatch, useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { TEST_ERROR_BOUNDARIES } from "../../../utils/error-boundary";
import useTranslationRoute from "../hooks/use-translation-route";
import { useActiveModulesInGroup, useIsBelowTheFold } from "../../../hooks/session.hooks";
import '../../../scss/live-event/base/session/session.scss';
import '../../admin/create/session/editor/session-main-below-the-fold.scss';
import { EngageCount } from "../../admin/create/session/editor/editor-main";

interface GroupedNavButtonsEditorProps {
	singleSession?: boolean;
	previewBundle?: BrandliveEvent;
	previewSession?: Session;
	moduleGroupsRefs: React.RefObject<HTMLParagraphElement>[];
	selectedTabUuid: string | undefined;
	setSelectedTabUuid: Dispatch<SetStateAction<string | undefined>>
	autoScroll: boolean
	setAutoScroll: Dispatch<SetStateAction<boolean>>
}
const GroupedNavButtonsSession: React.FC<GroupedNavButtonsEditorProps> = ({
	moduleGroupsRefs,
	singleSession,
	previewBundle,
	previewSession,
	selectedTabUuid,
	setSelectedTabUuid,
	autoScroll,
	setAutoScroll
}) => {
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const translationUpdates = useTypedSelector(state => state.LiveEventReducer.translationUpdates);

	const dispatch = useAppDispatch();

	const { sessionUuid: sessionParamUuid, language }: ParamsProps = useParams();

	const isPreview = !!(previewBundle || previewSession);
	const event = isPreview ? previewBundle : eventBundle;

	const sessionUuid = singleSession ? event?.sessions?.[0]?.uuid : sessionParamUuid;

	const session = useMemo(() => {
		if (singleSession) {
			return previewBundle?.sessions?.[0] ? previewBundle?.sessions?.[0] : eventBundle?.sessions?.[0];
		}
		if (previewSession) {
			return previewSession;
		}
		return eventBundle?.sessions?.find(session => session.uuid === sessionUuid);
	}, [singleSession, eventBundle, previewBundle, sessionUuid, previewSession]);

	useEffect(() => {
		if (!selectedTabUuid) {
			const defaultUUID = session?.module_grouping?.find(module => module.is_on && module.uuid)?.uuid;
			setSelectedTabUuid(defaultUUID);
		}
	}, [session, selectedTabUuid, setSelectedTabUuid]);

	const sessionDetailsV2FeatureFlagOn = featureFlags[FeatureFlagsEnum.session_details_v2];
	
	// When we autoscroll, we don't want the tabs to auto switch when starting in a different section than the selected section
	useEffect(() => {
		setTimeout(() => {
			if (autoScroll) {
				setAutoScroll(false);
			}
		}, 1000);
	}, [autoScroll, setAutoScroll]);

	
	const translationNamespace = useTranslationRoute();
	const { t } = useTranslate(["session", translationNamespace]);

	const showIcons = session?.layout?.tabs?.show_icons ?? false;

	const handleTabChange: ChangeEventHandler<HTMLInputElement> = useCallback(({ target }) => {
		if (TEST_ERROR_BOUNDARIES) dispatch(clearErrorBoundaryComponents());
		setSelectedTabUuid(target.value);
	}, [dispatch, setSelectedTabUuid]);

	const groupedNavButtons = useMemo(() => {
		return session?.module_grouping?.map((group, index) => {
			const { name, uuid, icon, is_on, modules, type } = group;

			const isSessionDetailsBelowTheFoldV2 = useIsBelowTheFold(session);

			
			const belowTheFoldNavigate = (moduleGroup: PageModuleGroupModules, idName: string, index: number) => () => {
				const container = moduleGroupsRefs[index];
				if (container?.current) {
					setAutoScroll(true);
					setSelectedTabUuid(moduleGroup.uuid);
					container.current.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			};

			// Breakout Rooms don't have modules.length so add check for BRs here too
			if (!is_on || (!modules.length && (name as TranslateString).base !== ModuleGroups.BreakoutRooms)) return null;

			const activeModules = useActiveModulesInGroup(session, group);

			if (!activeModules.length && type !== "breakout") return <></>;


			let _name: string;
			//Check to see if "_name" is a Translate string object, if so return the translated value, if not, then it is already a string. Use static translation if available or return the string
			const translateName = name as TranslateString;

			if (typeof translateName === "object") {
				_name = t(`${translationNamespace}:tabName.${translateName.base}`, translateName[language] as string ?? translateName.base);
			} else {
				_name = t(name as string) ?? name;
			}
			if (sessionDetailsV2FeatureFlagOn && isSessionDetailsBelowTheFoldV2 && group) {
				return (
					<li key={uuid}
						className={classNames({
							active: selectedTabUuid
								? uuid === selectedTabUuid
								: index === 0
						})}
					>
						<button
							onClick={belowTheFoldNavigate(group, _name, index)}
							className={classNames('clear no-style')}
						>
							<span>{_name}</span>
							<EngageCount group={group} session={session} />
						</button>
					</li>
				);
			}

			return (
				<li key={uuid}>
					<input
						type="radio"
						name="tab-navigation"
						id={`session-${uuid}`}
						value={uuid}
						onChange={handleTabChange}
						checked={uuid === selectedTabUuid}
					/>
					<label htmlFor={`session-${uuid}`}>
						<Icon name={icon || ""} size={16} color={classNames({ noIcon: !showIcons })} />
						<span>{_name}</span>
					</label>
				</li>
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		session?.module_grouping,
		session?.modules,
		handleTabChange,
		selectedTabUuid,
		showIcons,
		translationNamespace,
		t,
		language,
		translationUpdates // need to force re-render when translationUpdates has fired
	]);

	// display the profile bubble if there is no homepage but
	// registration is on or the session has more than 1 language
		return <>{groupedNavButtons}</>;
};

export default GroupedNavButtonsSession;